import { useState } from "react";
import { Download, X, Smartphone, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { usePWAInstall } from "@/hooks/usePWAInstall";

interface PWAInstallPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PWAInstallPrompt = ({ open, onOpenChange }: PWAInstallPromptProps) => {
  const { updateShortcutPermission } = useUserPreferences();
  const { isInstallable, isIOS, promptInstall, canPrompt, isInstalled } = usePWAInstall();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  const handleSkip = async () => {
    await updateShortcutPermission("dismissed");
    onOpenChange(false);
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (canPrompt) {
      const result = await promptInstall();
      if (result.outcome === "accepted") {
        await updateShortcutPermission("created");
        onOpenChange(false);
      }
    }
  };

  const handleIOSDone = async () => {
    await updateShortcutPermission("created");
    onOpenChange(false);
  };

  // Se já está instalado, não mostra o prompt
  if (isInstalled) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            Instalar HabitFrog
          </DialogTitle>
          <DialogDescription>
            {showIOSInstructions
              ? "Siga os passos para adicionar à tela inicial:"
              : "Instale o app para acesso rápido e notificações!"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showIOSInstructions ? (
            <>
              <div className="bg-muted rounded-lg p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Toque no botão Compartilhar</p>
                    <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                      <Share className="h-4 w-4" />
                      <span className="text-sm">No Safari, toque no ícone de compartilhar</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-medium">Adicionar à Tela de Início</p>
                    <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                      <Plus className="h-4 w-4" />
                      <span className="text-sm">Role para baixo e toque nesta opção</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Confirme a instalação</p>
                    <span className="text-sm text-muted-foreground">
                      Toque em "Adicionar" no canto superior direito
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Button onClick={handleIOSDone} className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Pronto, já instalei!
                </Button>
                <Button variant="ghost" onClick={handleSkip} className="w-full">
                  Fazer depois
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center py-4">
                <div className="relative">
                  <div className="w-20 h-20 bg-primary/20 rounded-2xl flex items-center justify-center shadow-lg">
                    <img 
                      src="/icons/icon-96x96.png" 
                      alt="HabitFrog" 
                      className="w-16 h-16 rounded-xl"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow">
                    <Download className="h-4 w-4 text-primary-foreground" />
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">
                  Com o app instalado você pode:
                </p>
                <ul className="text-sm text-left space-y-1 bg-muted rounded-lg p-3">
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span>
                    Acessar rapidamente da tela inicial
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span>
                    Receber notificações de lembretes
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span>
                    Usar offline quando não houver internet
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-primary">✓</span>
                    Experiência de app nativo
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-2">
                <Button 
                  onClick={handleInstall} 
                  className="w-full"
                  disabled={!isInstallable && !isIOS}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isIOS ? "Como instalar" : "Instalar agora"}
                </Button>
                <Button variant="ghost" onClick={handleSkip} className="w-full">
                  Pular
                </Button>
              </div>

              {!isInstallable && !isIOS && (
                <p className="text-xs text-center text-muted-foreground">
                  💡 Use o menu do navegador para "Adicionar à tela inicial"
                </p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
